import { useState, useEffect, useMemo } from 'react';
import { CinematicVideoPlayer } from '@/components/video/CinematicVideoPlayer';
import { VideoPreloader } from '@/components/video/VideoPreloader';
import { PhotoGallery } from '@/components/reveal/PhotoGallery';
import { useParams, Link } from 'react-router-dom';
import { Loader2, Heart, MessageCircle, Flag, Send, AlertTriangle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BrandLogo } from '@/components/BrandLogo';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { PremiumAnnouncementReveal } from '@/components/reveal/PremiumAnnouncementReveal';
import { ListenButton } from '@/components/announcement/ListenButton';
import { RecipientConversionBlock } from '@/components/recipient/RecipientConversionBlock';
import { RecipientViralCTA } from '@/components/recipient/RecipientViralCTA';
import { useLanguage } from '@/i18n/LanguageContext';

interface AnnouncementData {
  id: string;
  recipient_name: string;
  message: string;
  template: string;
  relationship: string;
  photo_urls: string[];
  is_anonymous: boolean;
  sender_display_name: string | null;
  courage_mode: string;
  created_at: string;
  animation_theme?: string;
  metadata?: Record<string, any>;
  reveal_policy?: string;
  reveal_after_days?: number;
  revealed_at?: string | null;
  published_at?: string | null;
}

function shouldShowSenderName(ann: AnnouncementData): boolean {
  if (ann.courage_mode === 'signed') return true;
  if (ann.courage_mode === 'reveal_when_ready') {
    return !!ann.revealed_at;
  }
  return !ann.is_anonymous;
}

function getRevealCountdown(ann: AnnouncementData, t: any): { pending: boolean; text: string } | null {
  if (ann.courage_mode !== 'reveal_when_ready') return null;
  if (ann.revealed_at) return null;
  if (ann.reveal_policy !== 'after_days' || !ann.reveal_after_days) return null;
  
  const publishedAt = ann.published_at ? new Date(ann.published_at) : new Date(ann.created_at);
  const revealDate = new Date(publishedAt.getTime() + ann.reveal_after_days * 24 * 60 * 60 * 1000);
  const now = new Date();
  
  if (now >= revealDate) {
    return { pending: true, text: t.reveal.revealingSoon };
  }
  
  const diffMs = revealDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  
  return { 
    pending: true, 
    text: diffDays === 1 
      ? t.reveal.revealInOneDay
      : t.reveal.revealInDays.replace('{days}', String(diffDays))
  };
}

function resolveSenderLabel(ann: AnnouncementData, t: any): string {
  if (!shouldShowSenderName(ann)) {
    return t.reveal.someoneWhoCares;
  }
  const meta = ann.metadata;
  return meta?.senderDisplayName || meta?.senderNickname || ann.sender_display_name || t.reveal.someoneWhoCares;
}

// Session cache to prevent "Announcement Not Found" on reopen
const CACHE_PREFIX = 'sin_token_cache_';
function cacheAnnouncement(token: string, data: AnnouncementData) {
  try { sessionStorage.setItem(CACHE_PREFIX + token, JSON.stringify(data)); } catch {}
}
function getCachedAnnouncement(token: string): AnnouncementData | null {
  try {
    const cached = sessionStorage.getItem(CACHE_PREFIX + token);
    return cached ? JSON.parse(cached) : null;
  } catch { return null; }
}

export default function AnnouncementTokenView() {
  const { t } = useLanguage();
  const { token } = useParams<{ token: string }>();
  const [announcement, setAnnouncement] = useState<AnnouncementData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revealDone, setRevealDone] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replySending, setReplySending] = useState(false);
  const [replySuccess, setReplySuccess] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDetails, setReportDetails] = useState('');
  const [reportSending, setReportSending] = useState(false);

  useEffect(() => {
    if (!token) return;

    const loadAnnouncement = async () => {
      setLoading(true);
      setError(null);

      try {
        const { data: annData, error: annError } = await supabase
          .rpc('get_announcement_by_token', { p_token: token } as any);

        const announcementData = Array.isArray(annData) ? annData[0] : annData;

        if (annError || !announcementData) {
          // Try session cache before showing error
          const cached = getCachedAnnouncement(token);
          if (cached) {
            setAnnouncement(cached);
            setLoading(false);
            return;
          }
          setError('This link is invalid or has expired.');
          setLoading(false);
          return;
        }

        if ((announcementData as any).moderation_status === 'blocked') {
          setError('This message has been removed for violating community guidelines.');
          setLoading(false);
          return;
        }

        // Mark as viewed (non-critical)
        try { await supabase.rpc('mark_recipient_viewed', { p_token: token } as any); } catch {}

        const data = announcementData as AnnouncementData;
        setAnnouncement(data);
        cacheAnnouncement(token, data);
      } catch (err) {
        console.error('Error loading announcement:', err);
        // Try session cache before showing error
        const cached = getCachedAnnouncement(token);
        if (cached) {
          setAnnouncement(cached);
          setLoading(false);
          return;
        }
        setError('Something went wrong. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadAnnouncement();
  }, [token]);

  const handleReply = async () => {
    if (!token || !announcement || !replyText.trim()) return;
    setReplySending(true);
    try {
      const { error } = await supabase
        .from('announcement_replies' as any)
        .insert({
          announcement_id: announcement.id,
          recipient_view_token: token,
          reply_text: replyText.trim(),
        } as any);

      if (error) throw error;
      
      // If reveal_policy is 'after_reply', trigger the reveal now
      if (announcement.reveal_policy === 'after_reply' && !announcement.revealed_at) {
        supabase.functions.invoke('process-reveals', {
          body: { trigger: 'reply', announcement_id: announcement.id, recipient_view_token: token },
        }).catch(() => {});
      }

      // Notify sender via email (fire-and-forget)
      const deviceId = localStorage.getItem('sin_device_id') || crypto.randomUUID();
      localStorage.setItem('sin_device_id', deviceId);
      supabase.functions.invoke('notify-reply', {
        body: { announcement_id: announcement.id, responder_identifier: `token_${deviceId}` },
      }).catch(err => console.warn('Reply notification failed:', err));
      
      setReplySuccess(true);
      setReplyText('');
      toast.success('Your reply has been sent privately.');
    } catch (err) {
      console.error('Reply error:', err);
      toast.error('Failed to send reply. Please try again.');
    } finally {
      setReplySending(false);
    }
  };

  const handleReport = async () => {
    if (!token || !announcement || !reportReason) return;
    setReportSending(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();

      const { error } = await supabase
        .from('announcement_reports')
        .insert({
          announcement_id: announcement.id,
          reporter_reason: reportReason,
          reporter_details: reportDetails || null,
          recipient_view_token: token,
        } as any);

      if (error) throw error;

      const { data: notifyResult, error: notifyError } = await supabase.functions.invoke('notify-report', {
        body: {
          announcement_id: announcement.id,
          reporter_reason: reportReason,
          reporter_details: reportDetails || null,
          reporter_token: token,
          announcement_url: typeof window !== 'undefined' ? window.location.href : null,
          submitted_at: new Date().toISOString(),
          source: 'user_report',
          user_id: user?.id ?? null,
          user_email: user?.email ?? null,
        },
      });

      if (notifyError || (notifyResult && typeof notifyResult === 'object' && 'success' in notifyResult && !notifyResult.success)) {
        throw notifyError || new Error('notify-report failed');
      }

      toast.success('Report submitted. Thank you for keeping our community safe.');
      setReportOpen(false);
      setReportReason('');
      setReportDetails('');
    } catch (err) {
      console.error('Report error:', err);
      toast.error('Failed to submit report.');
    } finally {
      setReportSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <AlertTriangle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-xl font-bold text-foreground mb-2">Message Not Found</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Link to="/">
            <Button variant="outline">Go to Say It NOW</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (!announcement) return null;

  const senderLabel = resolveSenderLabel(announcement, t);
  const revealCountdown = getRevealCountdown(announcement, t);
  const isRevealed = shouldShowSenderName(announcement);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-background/95 backdrop-blur">
        <div className="container flex h-14 items-center justify-center">
          <BrandLogo size="sm" />
        </div>
      </header>

      {/* Reveal countdown banner for pending reveals */}
      {revealCountdown && (
        <div className="bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-800 py-3 px-4">
          <div className="container max-w-lg mx-auto flex items-center justify-center gap-2">
            <Clock className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
            <p className="text-sm text-amber-700 dark:text-amber-300 font-medium text-center">
              {revealCountdown.text}
            </p>
          </div>
        </div>
      )}

      {/* Revealed identity banner */}
      {announcement.courage_mode === 'reveal_when_ready' && isRevealed && (
        <div className="bg-emerald-50 dark:bg-emerald-950/30 border-b border-emerald-200 dark:border-emerald-800 py-4 px-4">
          <div className="container max-w-lg mx-auto text-center">
            <p className="text-sm text-emerald-600 dark:text-emerald-400 mb-1">{t.reveal.identityRevealed}</p>
            <p className="text-lg font-bold text-foreground">
              {t.reveal.sentBy} {senderLabel}
            </p>
          </div>
        </div>
      )}

      {/* Premium animated reveal experience */}
      <div className="w-full">
        <VideoPreloader src={(announcement.metadata as Record<string, any> | null)?.videoUrl} photoUrls={announcement.photo_urls} />
        <PremiumAnnouncementReveal
          announcementId={announcement.id}
          animationTheme={announcement.animation_theme || 'elegant_floral'}
          recipientName={announcement.recipient_name}
          relationship={announcement.relationship as any}
          message={announcement.message}
          template={announcement.template}
          photoUrls={announcement.photo_urls?.length > 0 ? announcement.photo_urls : undefined}
          senderLabel={senderLabel}
          metadata={announcement.metadata as { chibiUrl?: string } | undefined}
          onRevealComplete={() => setRevealDone(true)}
          publishedDate={new Date(announcement.created_at).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        >
          {/* Attached photos gallery — tap to view full-screen + download */}
          <PhotoGallery
            photoUrls={announcement.photo_urls}
            transition={((announcement as any).metadata?.mediaTransition) ?? null}
            animationTheme={(announcement as any).animation_theme ?? null}
          />

          {/* Video message section — cinematic player */}
          {(() => {
            const meta = announcement.metadata as Record<string, any> | null;
            const videoUrl = meta?.videoUrl;
            if (!videoUrl) return null;
            console.log('[AnnouncementTokenView] rendering video attachment', { videoUrl });
            return <CinematicVideoPlayer src={videoUrl} poster={announcement.photo_urls?.[0]} />;
          })()}

          {/* Listen button for accessibility */}
          <div className="flex justify-center py-5">
            <ListenButton message={announcement.message} recipientToken={token} />
          </div>
        </PremiumAnnouncementReveal>
      </div>

      {/* ── Reply section — only after reveal ── */}
      {revealDone && (
        <div id="token-reply-section" className="container max-w-4xl px-4 mx-auto py-8">
          {!replySuccess ? (
            <div className="bg-muted/30 border border-border rounded-xl p-6 mb-6">
              <div className="flex items-center gap-2 mb-3">
                <MessageCircle className="h-5 w-5 text-primary" />
                <h3 className="text-sm font-medium text-foreground">Reply Privately</h3>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                {announcement.is_anonymous && !isRevealed
                  ? 'Your reply will be sent privately to the sender. Their identity remains protected.'
                  : 'Send a private reply to the sender.'}
              </p>
              <Textarea
                placeholder="Write your reply..."
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                className="mb-3 min-h-[80px]"
                maxLength={2000}
              />
              <Button
                onClick={handleReply}
                disabled={!replyText.trim() || replySending}
                className="gap-2"
                size="sm"
              >
                {replySending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Send Reply
              </Button>
            </div>
          ) : (
            <div className="bg-primary/5 border border-primary/20 rounded-xl p-6 mb-6 text-center">
              <Heart className="h-8 w-8 text-primary mx-auto mb-2" />
              <p className="text-sm text-foreground font-medium">Your reply has been sent.</p>
              <p className="text-xs text-muted-foreground mt-1">The sender will see your message privately.</p>
            </div>
          )}

          {/* Report button */}
          <div className="text-center">
            <Dialog open={reportOpen} onOpenChange={setReportOpen}>
              <DialogTrigger asChild>
                <Button variant="ghost" size="sm" className="text-muted-foreground gap-2">
                  <Flag className="h-3 w-3" />
                  Report this message
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Report This Message</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-2">
                  <div>
                    <Label className="text-sm text-muted-foreground mb-2 block">Reason</Label>
                    <Select value={reportReason} onValueChange={setReportReason}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a reason" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="harassment">Harassment or threats</SelectItem>
                        <SelectItem value="spam">Spam</SelectItem>
                        <SelectItem value="inappropriate">Inappropriate content</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground mb-2 block">Details (optional)</Label>
                    <Textarea
                      placeholder="Tell us more..."
                      value={reportDetails}
                      onChange={(e) => setReportDetails(e.target.value)}
                      maxLength={500}
                    />
                  </div>
                  <Button
                    onClick={handleReport}
                    disabled={!reportReason || reportSending}
                    variant="destructive"
                    className="w-full"
                  >
                    {reportSending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Submit Report'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <p className="text-center text-xs text-muted-foreground mt-8">
            Anonymous messages are moderated and traceable internally for safety.
          </p>
        </div>
      )}

      {revealDone && (
        <RecipientViralCTA
          source="token_view"
          senderName={isRevealed ? senderLabel : undefined}
        />
      )}
    </div>
  );
}

function Label({ children, className, ...props }: React.LabelHTMLAttributes<HTMLLabelElement>) {
  return <label className={className} {...props}>{children}</label>;
}
